// Admin v2 — Verseny kezelés (Figma ADM_14, 14b, 14c, 14d).
// Versenyek: tournaments (mobil-séma + bővítés: format, bracketType, venueName, doubleOut/doubleIn, prizePool{total,first,second}).
// Bracket: a tournaments dokumentum `bracket.rounds` mezője (egyenes kiesés, CP szerinti kiemeléssel).
// Általános beállítások: app_settings/tournaments.
import React, { useEffect, useMemo, useState } from 'react';
import { addDoc, collection, doc, getDocs, onSnapshot, serverTimestamp, setDoc, Timestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Icon } from '../icons';
import { fmtInt, toDate } from '../data';
import {
  Badge, Button, Card, Empty, Field, IconTile, Input, Modal, PageHeader, SectionTitle, Segmented, Select, Spinner, Tabs, TextArea, Toggle, Tone, useToast,
} from '../ui';

type Tab = 'list' | 'bracket' | 'settings';
type TStatus = 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
interface Match { p1: string | null; p2: string | null; s1: number | null; s2: number | null }
interface T {
  id: string; name: string; description: string; type: string; status: TStatus; start: Date | null; end: Date | null; max: number; participants: string[];
  gameMode: string; venueName: string; bracketType: string; prize: number; first: number; second: number; doubleOut: boolean; doubleIn: boolean; randomCricket: boolean;
  rounds: Match[][]; winnerId: string | null;
}
const ST: Record<TStatus, { t: string; tone: Tone }> = { upcoming: { t: 'Közelgő', tone: 'cyan' }, ongoing: { t: 'Folyamatban', tone: 'green' }, completed: { t: 'Befejezett', tone: 'gray' }, cancelled: { t: 'Lemondva', tone: 'red' } };
const ROUND_NAMES = (n: number, i: number) => { const left = n - i; return left === 1 ? 'Döntő' : left === 2 ? 'Elődöntő' : left === 3 ? 'Negyeddöntő' : `${i + 1}. forduló`; };
const BRACKET_HU: Record<string, string> = { singleElimination: 'Egyenes kiesés', doubleElimination: 'Dupla kiesés', roundRobin: 'Körmérkőzés' };
const fmtD = (d: Date | null) => (d ? `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}.` : '');

function fromDoc(id: string, v: Record<string, any>): T { // eslint-disable-line @typescript-eslint/no-explicit-any
  const pp = v.prizePool;
  const rounds: Match[][] = Array.isArray(v.bracket?.rounds) ? v.bracket.rounds.map((r: { matches: Match[] }) => r.matches ?? []) : [];
  return {
    id, name: v.name ?? 'Verseny', description: v.description ?? '', type: v.type ?? 'online', status: (ST[v.status as TStatus] ? v.status : 'upcoming') as TStatus,
    start: toDate(v.startDate), end: toDate(v.endDate), max: Number(v.maxParticipants ?? 0), participants: v.participantIds ?? [],
    gameMode: v.gameMode ?? '501', venueName: v.venueName ?? '', bracketType: BRACKET_HU[v.bracketType ?? v.format] ?? v.bracketType ?? v.format ?? 'Egyenes kiesés',
    prize: typeof pp === 'number' ? pp : Number(pp?.total ?? 0), first: Number(pp?.first ?? 0), second: Number(pp?.second ?? 0),
    doubleOut: v.doubleOut !== false, doubleIn: v.doubleIn === true, randomCricket: v.randomCricket === true, rounds, winnerId: v.winnerId ?? null,
  };
}

/** Egyenes kieséses ág CP szerinti kiemeléssel; a hiányzó helyek továbbjutást (bye) jelentenek. */
function buildBracket(players: string[], cp: Record<string, number>): Match[][] {
  const seeded = [...players].sort((a, b) => (cp[b] ?? 0) - (cp[a] ?? 0));
  let size = 2;
  while (size < seeded.length) size *= 2;
  const first: Match[] = [];
  for (let i = 0; i < size / 2; i++) first.push({ p1: seeded[i] ?? null, p2: seeded[size - 1 - i] ?? null, s1: null, s2: null });
  const rounds: Match[][] = [first];
  for (let n = size / 4; n >= 1; n /= 2) rounds.push(Array.from({ length: n }, () => ({ p1: null, p2: null, s1: null, s2: null })));
  return propagate(rounds);
}

/** A győztesek (és a bye-ok) továbbléptetése a következő körbe. */
function propagate(rounds: Match[][]): Match[][] {
  const r = rounds.map((x) => x.map((m) => ({ ...m })));
  for (let i = 0; i < r.length - 1; i++) {
    r[i].forEach((m, j) => {
      const w = winnerOf(m, i === 0);
      const next = r[i + 1][Math.floor(j / 2)];
      if (j % 2 === 0) next.p1 = w; else next.p2 = w;
    });
  }
  return r;
}
function winnerOf(m: Match, allowBye: boolean): string | null {
  if (allowBye && m.p1 && !m.p2) return m.p1;
  if (allowBye && m.p2 && !m.p1) return m.p2;
  if (m.s1 === null || m.s2 === null || m.s1 === m.s2) return null;
  return m.s1 > m.s2 ? m.p1 : m.p2;
}
const toStore = (rounds: Match[][]) => ({ rounds: rounds.map((matches) => ({ matches })) });

export default function TournamentsPage() {
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('list');
  const [list, setList] = useState<T[] | null>(null);
  const [users, setUsers] = useState<Record<string, { name: string; cp: number }>>({});
  const [edit, setEdit] = useState<T | 'new' | null>(null);
  const [selId, setSelId] = useState<string | null>(null);

  useEffect(() => onSnapshot(collection(db, 'tournaments'), (s) => setList(s.docs.map((d) => fromDoc(d.id, d.data()))
    .sort((a, b) => (b.start?.getTime() ?? 0) - (a.start?.getTime() ?? 0))), () => setList([])), []);
  useEffect(() => {
    getDocs(collection(db, 'users')).then((s) => {
      const m: Record<string, { name: string; cp: number }> = {};
      s.docs.forEach((d) => { const v = d.data(); m[d.id] = { name: v.username || v.displayName || 'Játékos', cp: Number(v.dqScore ?? 0) }; });
      setUsers(m);
    }).catch(() => undefined);
  }, []);

  const start = async (t: T) => {
    if (t.participants.length < 2) { toast('Legalább 2 nevező kell az indításhoz.', 'red'); return; }
    const cp = Object.fromEntries(Object.entries(users).map(([k, v]) => [k, v.cp]));
    try {
      await updateDoc(doc(db, 'tournaments', t.id), { status: 'ongoing', bracket: toStore(buildBracket(t.participants, cp)), startedAt: serverTimestamp() });
      toast('Verseny elindítva, a bracket elkészült.');
      setSelId(t.id); setTab('bracket');
    } catch { toast('Az indítás nem sikerült.', 'red'); }
  };

  const sel = list?.find((t) => t.id === selId) ?? list?.find((t) => t.status === 'ongoing') ?? null;

  return (
    <div>
      <PageHeader title="Verseny kezelés" sub={tab === 'bracket' && sel ? `${sel.name} · ${sel.bracketType}` : 'Versenyek, bracketek és beállítások'}
        right={tab === 'bracket' ? <Button kind="ghost" icon="ArrowLeft" onClick={() => setTab('list')}>Vissza a listához</Button> : <Button icon="Plus" onClick={() => setEdit('new')}>Új verseny</Button>} />
      <Tabs<Tab> value={tab} onChange={setTab} options={[{ value: 'list', label: 'Versenyek' }, { value: 'bracket', label: 'Bracket-ek' }, { value: 'settings', label: 'Beállítások' }]} />

      {tab === 'list' && (list === null ? <Spinner /> : list.length === 0 ? <Card><Empty>Még nincs verseny.</Empty></Card> : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {list.map((t) => (
            <Card key={t.id} className="!p-5">
              <div className="flex items-start gap-3">
                <IconTile icon="Trophy" tone="gold" size={40} />
                <div className="flex-1 min-w-0">
                  <div className="text-[15px] font-semibold text-white truncate">{t.name}</div>
                  <div className="text-[12px] text-[#8a8f99] truncate">{t.type === 'online' ? 'Online' : `Helyszín (Club) · ${t.venueName || '—'}`} · {fmtD(t.start)}</div>
                </div>
                <Badge tone={ST[t.status].tone}>{ST[t.status].t}</Badge>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-4">
                {[['Játékmód', t.gameMode], ['Résztvevők', `${t.participants.length} / ${t.max || '∞'}`], ['Nyereményalap', t.prize ? `${fmtInt(t.prize)} Ft` : '—']].map(([k, v]) => (
                  <div key={k} className="px-3 py-2 rounded-xl bg-[#1d232a]"><div className="text-[11px] text-[#8a8f99]">{k}</div><div className="text-[14px] font-bold text-white">{v}</div></div>
                ))}
              </div>
              <div className="flex gap-2 mt-3 flex-wrap">
                {t.doubleOut && <Badge tone="cyan">Double Out</Badge>}
                {t.doubleIn && <Badge tone="cyan">Double In</Badge>}
                <Badge tone="purple">{t.bracketType}</Badge>
              </div>
              <div className="flex justify-end gap-2 mt-4 flex-wrap">
                <Button kind="ghost" icon="ChartBar" className="!h-[32px] !text-[13px]" onClick={() => { setSelId(t.id); setTab('bracket'); }}>Bracket</Button>
                <Button kind="ghost" icon="PencilSimpleLine" className="!h-[32px] !text-[13px]" onClick={() => setEdit(t)}>Szerkesztés</Button>
                {t.status === 'upcoming' && <Button icon="PlayCircle" className="!h-[32px] !text-[13px]" onClick={() => start(t)}>Verseny indítása</Button>}
                {t.status === 'ongoing' && <span className="inline-flex items-center gap-1.5 h-[32px] px-3.5 rounded-full bg-[#2bc48a] text-white text-[13px] font-semibold"><Icon name="Lightning" size={14} />Folyamatban</span>}
              </div>
            </Card>
          ))}
        </div>
      ))}

      {tab === 'bracket' && (
        <div>
          {list && list.length > 0 && (
            <div className="mb-4 w-full max-w-[420px]"><Select value={sel?.id ?? ''} onChange={setSelId} options={list.map((t) => ({ value: t.id, label: `${t.name} · ${ST[t.status].t}` }))} /></div>
          )}
          {sel ? <Bracket t={sel} names={users} onStart={() => start(sel)} /> : <Card><Empty>Válassz versenyt.</Empty></Card>}
        </div>
      )}

      {tab === 'settings' && <SettingsTab />}
      {edit && <TournamentModal t={edit === 'new' ? null : edit} onClose={() => setEdit(null)} />}
    </div>
  );
}

function Bracket({ t, names, onStart }: { t: T; names: Record<string, { name: string; cp: number }>; onStart: () => void }) {
  const toast = useToast();
  const [score, setScore] = useState<{ r: number; m: number } | null>(null);
  if (!t.rounds.length) {
    return (
      <Card className="!p-8 text-center">
        <p className="text-[14px] text-[#B8B8C3]">Ehhez a versenyhez még nincs bracket. {t.participants.length} nevező.</p>
        {t.status === 'upcoming' && <div className="mt-4"><Button icon="PlayCircle" onClick={onStart}>Verseny indítása és bracket generálása</Button></div>}
      </Card>
    );
  }
  const nm = (id: string | null) => (id ? names[id]?.name ?? 'Játékos' : '—');
  const final = t.rounds[t.rounds.length - 1][0];
  const champ = winnerOf(final, false);
  const save = async (rounds: Match[][], winner: string | null) => {
    try {
      await updateDoc(doc(db, 'tournaments', t.id), { bracket: toStore(rounds), ...(winner ? { status: 'completed', winnerId: winner, endDate: Timestamp.now() } : {}) });
      toast(winner ? 'Eredmény mentve — a verseny lezárult.' : 'Eredmény mentve.');
    } catch { toast('A mentés nem sikerült.', 'red'); }
  };
  const cur = score ? t.rounds[score.r][score.m] : null;
  return (
    <Card className="!p-5 overflow-x-auto">
      <div className="flex gap-6 min-w-max items-stretch">
        {t.rounds.map((round, ri) => (
          <div key={ri} className="flex flex-col w-[220px]">
            <div className="text-[11px] font-semibold uppercase text-[#8a8f99] mb-3">{ROUND_NAMES(t.rounds.length, ri)}</div>
            <div className="flex-1 flex flex-col justify-around gap-3">
              {round.map((m, mi) => {
                const w = winnerOf(m, ri === 0);
                const clickable = Boolean(m.p1 && m.p2 && t.status !== 'completed');
                return (
                  <button key={mi} type="button" disabled={!clickable} onClick={() => setScore({ r: ri, m: mi })}
                    className={`rounded-xl border border-[#31383D] bg-[#1d232a] text-left overflow-hidden ${clickable ? 'hover:border-[#00AFF2]/60' : 'cursor-default'}`}>
                    {[[m.p1, m.s1], [m.p2, m.s2]].map(([p, sc], k) => (
                      <div key={k} className={`flex items-center justify-between px-3 h-[34px] text-[13px] ${k ? 'border-t border-[#31383D]' : ''} ${p && w === p ? 'text-white font-semibold' : 'text-[#8a8f99]'}`}>
                        <span className="truncate">{p ? nm(p as string) : ri === 0 ? 'továbbjutás' : '—'}</span>
                        <span className={p && w === p ? 'text-[#2bc48a]' : ''}>{sc ?? ''}</span>
                      </div>
                    ))}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        <div className="flex flex-col items-center justify-center w-[160px] text-center">
          <Icon name="Trophy" size={40} className="text-[#d9a514]" />
          <div className="text-[11px] uppercase text-[#8a8f99] mt-2">Győztes</div>
          <div className="text-[14px] font-semibold text-white mt-1">{champ ? nm(champ) : 'Még nem dőlt el'}</div>
        </div>
      </div>
      {score && cur && <ScoreModal title={`${ROUND_NAMES(t.rounds.length, score.r)} · ${nm(cur.p1)} vs ${nm(cur.p2)}`} a={nm(cur.p1)} b={nm(cur.p2)} m={cur}
        onClose={() => setScore(null)} onSave={async (s1, s2) => {
          const rounds = t.rounds.map((r) => r.map((x) => ({ ...x })));
          rounds[score.r][score.m] = { ...rounds[score.r][score.m], s1, s2 };
          // a későbbi körök érintett eredményeit töröljük, ha a továbbjutó változott
          for (let i = score.r + 1; i < rounds.length; i++) rounds[i] = rounds[i].map((x) => ({ ...x, ...(x.p1 === null || x.p2 === null ? { s1: null, s2: null } : {}) }));
          const next = propagate(rounds);
          const fin = next[next.length - 1][0];
          await save(next, score.r === next.length - 1 ? winnerOf(fin, false) : null);
          setScore(null);
        }} />}
    </Card>
  );
}

function ScoreModal({ title, a, b, m, onClose, onSave }: { title: string; a: string; b: string; m: Match; onClose: () => void; onSave: (s1: number, s2: number) => Promise<void> }) {
  const [s1, setS1] = useState(String(m.s1 ?? ''));
  const [s2, setS2] = useState(String(m.s2 ?? ''));
  const ok = s1 !== '' && s2 !== '' && s1 !== s2;
  return (
    <Modal title="Mérkőzés eredménye" onClose={onClose} width={460}
      footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button disabled={!ok} onClick={() => onSave(Number(s1), Number(s2))}>Eredmény mentése</Button></>}>
      <p className="text-[13px] text-[#B8B8C3] mb-4">{title}</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label={`${a} – nyert legek`}><Input inputMode="numeric" value={s1} onChange={(e) => setS1(e.target.value.replace(/[^\d]/g, ''))} /></Field>
        <Field label={`${b} – nyert legek`}><Input inputMode="numeric" value={s2} onChange={(e) => setS2(e.target.value.replace(/[^\d]/g, ''))} /></Field>
      </div>
      {s1 !== '' && s1 === s2 && <p className="mt-3 text-[12px] text-[#d9a514]">Kieséses ágban nem lehet döntetlen.</p>}
    </Modal>
  );
}

const toLocal = (d: Date | null) => (d ? new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '');

function TournamentModal({ t, onClose }: { t: T | null; onClose: () => void }) {
  const toast = useToast();
  const { user } = useAuth();
  const [clubs, setClubs] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => { getDocs(collection(db, 'clubs')).then((s) => setClubs(s.docs.map((d) => ({ id: d.id, name: String(d.data().name ?? 'Klub') })))).catch(() => undefined); }, []);
  const [f, setF] = useState({
    name: t?.name ?? '', description: t?.description ?? '', type: t?.type === 'online' ? 'online' : t ? 'offline' : 'offline', gameMode: t?.gameMode ?? '501',
    status: t?.status ?? 'upcoming' as TStatus, max: String(t?.max ?? 32), venue: t?.venueName ?? '', bracketType: t?.bracketType ?? 'Egyenes kiesés',
    start: toLocal(t?.start ?? null), end: toLocal(t?.end ?? null), prize: String(t?.prize || ''), first: String(t?.first || ''), second: String(t?.second || ''),
    doubleOut: t?.doubleOut ?? true, doubleIn: t?.doubleIn ?? false, randomCricket: t?.randomCricket ?? false,
  });
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const num = (v: string) => Number(v.replace(/[^\d]/g, '')) || 0;
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim() || !f.start) { toast('A név és a kezdés dátuma kötelező.', 'red'); return; }
    setBusy(true);
    const club = clubs.find((c) => c.name === f.venue);
    const data = {
      name: f.name.trim(), description: f.description.trim(), type: f.type, gameMode: f.gameMode, status: f.status, maxParticipants: num(f.max),
      venueName: f.type === 'online' ? '' : f.venue, clubId: f.type === 'online' ? null : club?.id ?? null, bracketType: f.bracketType,
      startDate: Timestamp.fromDate(new Date(f.start)), endDate: f.end ? Timestamp.fromDate(new Date(f.end)) : null,
      prizePool: { total: num(f.prize), first: num(f.first), second: num(f.second) }, doubleOut: f.doubleOut, doubleIn: f.doubleIn, randomCricket: f.randomCricket, isPublic: true,
    };
    try {
      if (t) await updateDoc(doc(db, 'tournaments', t.id), data);
      else await addDoc(collection(db, 'tournaments'), { ...data, participantIds: [], createdBy: user?.uid ?? 'admin', createdAt: serverTimestamp() });
      toast('Verseny mentve.'); onClose();
    } catch { toast('A mentés nem sikerült.', 'red'); }
    setBusy(false);
  };
  const Sw = ({ k, label }: { k: 'doubleOut' | 'doubleIn' | 'randomCricket'; label: string }) => (
    <span className="inline-flex items-center gap-2 text-[12px] text-white"><Toggle checked={f[k]} onChange={(v) => set(k, v)} />{label}</span>
  );
  return (
    <Modal title={t ? 'Verseny szerkesztése' : 'Új verseny'} onClose={onClose} width={680}
      footer={<><Button kind="ghost" onClick={onClose}>Mégse</Button><Button disabled={busy} onClick={save}>Mentés</Button></>}>
      <div className="grid grid-cols-3 gap-x-3 gap-y-4">
        <Field label="Verseny neve *" className="col-span-3"><Input value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="Spring Championship 2026" /></Field>
        <Field label="Leírás" className="col-span-3"><TextArea rows={2} value={f.description} onChange={(e) => set('description', e.target.value)} placeholder="Részletes leírás a versenyről..." /></Field>
        <Field label="Típus *"><Select value={f.type} onChange={(v) => set('type', v)} options={[{ value: 'offline', label: 'Helyszín (Club)' }, { value: 'online', label: 'Online' }]} /></Field>
        <Field label="Játékmód *"><Select value={f.gameMode} onChange={(v) => set('gameMode', v)} options={['501', '301', '701', 'Cricket'].map((x) => ({ value: x, label: x }))} /></Field>
        <Field label="Státusz *"><Select value={f.status} onChange={(v) => set('status', v as TStatus)} options={Object.entries(ST).map(([k, v]) => ({ value: k, label: v.t }))} /></Field>
        <Field label="Max. résztvevők *"><Input inputMode="numeric" value={f.max} onChange={(e) => set('max', e.target.value.replace(/[^\d]/g, ''))} /></Field>
        <Field label="Helyszín *"><Select value={f.venue} onChange={(v) => set('venue', v)} options={[{ value: '', label: f.type === 'online' ? 'Online' : 'Válassz klubot' }, ...clubs.map((c) => ({ value: c.name, label: c.name }))]} /></Field>
        <Field label="Bracket típus *"><Select value={f.bracketType} onChange={(v) => set('bracketType', v)} options={['Egyenes kiesés', 'Dupla kiesés', 'Körmérkőzés'].map((x) => ({ value: x, label: x }))} /></Field>
        <Field label="Kezdés dátuma *" className="col-span-3 sm:col-span-1"><Input type="datetime-local" value={f.start} onChange={(e) => set('start', e.target.value)} className="[color-scheme:dark]" /></Field>
        <Field label="Befejezés dátuma *" className="col-span-3 sm:col-span-2"><Input type="datetime-local" value={f.end} onChange={(e) => set('end', e.target.value)} className="[color-scheme:dark]" /></Field>
        <Field label="Nyereményalap (Ft)"><Input inputMode="numeric" value={f.prize} onChange={(e) => set('prize', e.target.value.replace(/[^\d]/g, ''))} placeholder="150 000" /></Field>
        <Field label="1. helyezés"><Input inputMode="numeric" value={f.first} onChange={(e) => set('first', e.target.value.replace(/[^\d]/g, ''))} placeholder="30 000 Ft" /></Field>
        <Field label="2. helyezés"><Input inputMode="numeric" value={f.second} onChange={(e) => set('second', e.target.value.replace(/[^\d]/g, ''))} placeholder="15 000 Ft" /></Field>
      </div>
      <div className="flex items-center gap-4 flex-wrap mt-4">
        <span className="text-[12px] text-[#8a8f99]">Játék beállítások:</span>
        <Sw k="doubleOut" label="Double Out" /><Sw k="doubleIn" label="Double In" /><Sw k="randomCricket" label="Random Cricket" />
      </div>
      {f.bracketType !== 'Egyenes kiesés' && <p className="mt-3 text-[12px] text-[#8a8f99]">A bracket-generátor jelenleg egyenes kieséses ágat készít.</p>}
    </Modal>
  );
}

function SettingsTab() {
  const toast = useToast();
  const DEF = { autoAdvance: true, lateRegistration: false, paymentRequired: true, liveStream: true, publicBracket: true, notifications: true, maxMatchMin: 45, breakMin: 10, defaultMode: '501', bestOf: 'Bo5' };
  const [s, setS] = useState(DEF);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => onSnapshot(doc(db, 'app_settings', 'tournaments'), (d) => { setS({ ...DEF, ...(d.data() ?? {}) }); setLoaded(true); }, () => setLoaded(true)), []); // eslint-disable-line react-hooks/exhaustive-deps
  const rows: [keyof typeof DEF, string, string][] = [
    ['autoAdvance', 'Automatikus továbbjutás', 'Győztesek automatikus továbbjuttatása a következő fordulóba'],
    ['lateRegistration', 'Késői regisztráció', 'Játékosok regisztrálhatnak a határidő után is'],
    ['paymentRequired', 'Fizetés kötelező', 'Nevezési díj fizetése szükséges a regisztrációhoz'],
    ['liveStream', 'Élő közvetítés', 'Verseny mérkőzések élőben közvetíthetők'],
    ['publicBracket', 'Bracket nyilvános megjelenítése', 'A bracket mindenki számára látható'],
    ['notifications', 'Értesítések küldése', 'Push értesítések mérkőzés frissítésekről'],
  ];
  const save = async () => { try { await setDoc(doc(db, 'app_settings', 'tournaments'), s, { merge: true }); toast('Beállítások mentve.'); } catch { toast('A mentés nem sikerült.', 'red'); } };
  const best = useMemo(() => ['Bo1', 'Bo3', 'Bo5', 'Bo7'].map((x) => ({ value: x, label: x })), []);
  if (!loaded) return <Spinner />;
  return (
    <div>
      <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-4 items-start">
        <Card className="!p-5">
          <SectionTitle>Általános beállítások</SectionTitle>
          <div className="flex flex-col gap-2.5">
            {rows.map(([k, t, sub]) => (
              <div key={k} className="flex items-center justify-between gap-4 px-3 py-3 rounded-xl bg-[#1d232a]">
                <div><div className="text-[13px] font-semibold text-white">{t}</div><div className="text-[11px] text-[#8a8f99] mt-0.5">{sub}</div></div>
                <Toggle checked={Boolean(s[k])} onChange={(v) => setS((x) => ({ ...x, [k]: v }))} />
              </div>
            ))}
          </div>
        </Card>
        <Card className="!p-5">
          <SectionTitle>Mérkőzés beállítások</SectionTitle>
          <div className="flex flex-col gap-4">
            <div><Field label="Max mérkőzés időtartam (perc)"><Input inputMode="numeric" value={String(s.maxMatchMin)} onChange={(e) => setS({ ...s, maxMatchMin: Number(e.target.value.replace(/[^\d]/g, '')) || 0 })} /></Field><div className="mt-1.5 text-[11px] text-[#8a8f99]">Maximálisan megengedett idő egy mérkőzéshez</div></div>
            <Field label="Szünet fordulók között (perc)"><Input inputMode="numeric" value={String(s.breakMin)} onChange={(e) => setS({ ...s, breakMin: Number(e.target.value.replace(/[^\d]/g, '')) || 0 })} /></Field>
            <Field label="Alapértelmezett játékmód"><Select value={s.defaultMode} onChange={(v) => setS({ ...s, defaultMode: v })} options={['501', '301', '701', 'Cricket'].map((x) => ({ value: x, label: x }))} /></Field>
            <div><div className="text-[13px] font-medium text-[#B8B8C3] mb-2">Best of</div><Segmented value={s.bestOf} onChange={(v) => setS({ ...s, bestOf: v })} options={best} /></div>
          </div>
        </Card>
      </div>
      <div className="flex justify-end mt-4"><Button icon="Check" onClick={save}>Mentés</Button></div>
    </div>
  );
}
